
import { defineComponent } from 'vue'
import { uniqueId, throttle } from 'lodash'
import { createPopper, Instance as PopperInstance } from '@popperjs/core'
import VueSelect from '@nextrequestco/vue-select'
import NrIconButton from '../NrIconButton/nr-icon-button.vue'
import { removeMarkup } from './remove-markup'
import NrFormLabel from '../form-elements/nr-form-label.vue'
import omit from 'lodash/omit'

interface SelectOption {
  label?: string
  value?: string | number
  [key: string]: any
}

interface SelectComponent {
  $refs: {
    toggle: HTMLElement
    dropdownMenu: HTMLElement
  }
  getOptionLabel: (option: SelectOption) => string
  selectedValue: Array<object>
}

const COMPONENT_NAME = 'NrSelect'

export default defineComponent({
  name: COMPONENT_NAME,

  components: { VueSelect, NrIconButton, NrFormLabel },

  mounted() {
    this.listboxEl?.addEventListener(
      'scroll',
      this.handleListboxScroll.bind(this)
    )
  },

  beforeDestroy() {
    this.popper?.destroy()
    this.listboxEl?.removeEventListener(
      'scroll',
      this.handleListboxScroll.bind(this)
    )
  },

  computed: {
    attrs() {
      // Omit open and option:created events because we need to intercept and not call them twice
      return omit(this.$attrs, ['onOpen', 'onOption:created'])
    },

    listboxEl(): HTMLElement | undefined {
      return (this.$refs.select as SelectComponent)?.$refs.dropdownMenu
    },

    id(): string {
      return uniqueId(`${COMPONENT_NAME}-`)
    },

    searchClassnames() {
      return [
        'input',
        {
          'is-focused': this.searchFocused
        }
      ]
    },

    sanitizedOptions(): (string | SelectOption)[] {
      return ((this.$attrs.options as (string | SelectOption)[]) || []).map(
        (option) => this.sanitizeOption(option)
      )
    }
  },

  props: {
    hideLabel: { type: Boolean, default: false },
    hasMore: { type: Boolean, default: false },
    hasDelete: { type: Boolean, default: false }
  },

  data() {
    return {
      showRequiredText: false,
      searchFocused: false,
      searchBlurred: true,
      popper: null as PopperInstance | null
    }
  },

  methods: {
    validateOnFocusOut() {
      const selectedValue = (this.$refs.select as SelectComponent)
        ?.selectedValue
      this.showRequiredText = this.$attrs?.required && !selectedValue?.length
    },

    validateOnInput(val: Array<any> | object) {
      this.showRequiredText =
        this.$attrs?.multiple && Array.isArray(val)
          ? this.$attrs?.required && !val.length
          : this.$attrs?.required && !val
    },

    sanitizeOption(option: string | SelectOption): string | SelectOption {
      const label = (this.$attrs.label as string) || 'label'
      if (typeof option === 'string') {
        return removeMarkup(option)
      } else {
        return {
          ...option,
          [label]: removeMarkup(option[label])
        }
      }
    },

    handleListboxScroll: throttle(function (this: any) {
      const loaderEl = this.listboxEl?.querySelector('.infinite-scroll-trigger')
      if (!loaderEl) {
        return
      }
      const containerY =
        this.listboxEl.getBoundingClientRect().bottom +
        loaderEl.getBoundingClientRect().height
      const triggerY = loaderEl.getBoundingClientRect().top

      if (triggerY <= containerY) {
        this.$emit('load-more')
      }
    }, 400),

    handleSearchFocus() {
      this.searchFocused = true
      this.searchBlurred = false
    },

    handleSearchBlur() {
      this.searchFocused = false
      this.searchBlurred = true
    },

    handleSelectedOptionClick(option: {
      option: SelectOption
      deselect: (option: SelectOption) => void
    }) {
      const deselectedOption = option.option
      option.deselect(deselectedOption)
    },

    parseOptionLabel(option: SelectOption): string {
      return (
        (this.$refs.select as SelectComponent)?.getOptionLabel(option) ??
        'option'
      )
    },

    getOptionKey(option: SelectOption): string | number {
      if (typeof option === 'object' && option.value) {
        return option.value
      } else {
        return JSON.stringify(option)
      }
    },

    onInput(val: any) {
      this.validateOnInput(val)
      this.$emit('input', val)
    },

    onDelete(val: SelectOption) {
      this.$emit('on-delete', val)
    },

    onOptionCreate(newOption: SelectOption) {
      const option = this.sanitizeOption(newOption)
      this.$emit('option:created', option)
    },

    handleOpen() {
      const { width } = (this.$el as HTMLElement).getBoundingClientRect()
      if (this.listboxEl) {
        this.listboxEl.style.width = `${width}px`
      }
      this.popper?.update()
      this.$emit('open')
    },

    withPopper(dropdownList: HTMLElement, component: SelectComponent) {
      this.popper = createPopper(component.$refs.toggle, dropdownList)
      return () => this.popper?.destroy()
    }
  }
})
